import React from "react";
import { useLanguage } from "../i18n";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

export const BUSINESS_TYPES = ["professionista", "studio", "distributore", "impresa", "altro"];
export const EMPTY_PROFILE = {
  first_name: "", last_name: "", phone: "", user_type: "", company_name: "", company_city: "", website: "", invoice_requested: false,
  billing: { street: "", street_number: "", zip: "", city: "", province: "", fiscal_code: "", vat: "", company_fiscal_code: "", sdi: "", pec: "", cuu: "", cig: "", cup: "" },
};
export const mergeProfile = (p) => ({ ...EMPTY_PROFILE, ...(p || {}), billing: { ...EMPTY_PROFILE.billing, ...(p?.billing || {}) } });

const lbl = "text-xs uppercase tracking-[0.15em] font-semibold text-inksoft";
const inp = "rounded-sm border-ink/15 focus-visible:ring-amberl h-11";
const sect = "text-[11px] uppercase tracking-[0.2em] font-semibold text-sagedark";

const F = ({ id, label, value, onChange, required, type = "text", maxLength, placeholder }) => (
  <div className="space-y-2">
    <Label className={lbl}>{label}</Label>
    <Input data-testid={`profile-${id}`} type={type} required={required} maxLength={maxLength} placeholder={placeholder} value={value} onChange={(e) => onChange(e.target.value)} className={inp} />
  </div>
);

export const ProfileFields = ({ value, onChange, invoiceMode = "toggle", email, onEmailChange, idPrefix = "" }) => {
  const { t } = useLanguage();
  const p = t.profile;
  const v = mergeProfile(value);
  const set = (k) => (val) => onChange({ ...v, [k]: val });
  const setB = (k) => (val) => onChange({ ...v, billing: { ...v.billing, [k]: val } });
  const isBusiness = BUSINESS_TYPES.includes(v.user_type);
  const isPA = v.user_type === "pa";
  const showInvoice = invoiceMode === "required" || v.invoice_requested;

  return (
    <div data-testid={`${idPrefix}profile-fields`} className="space-y-6">
      <div className="grid sm:grid-cols-2 gap-6">
        <F id="first-name" label={p.firstName} value={v.first_name} onChange={set("first_name")} required />
        <F id="last-name" label={p.lastName} value={v.last_name} onChange={set("last_name")} required />
        {onEmailChange && <F id="email" label={p.email} type="email" value={email} onChange={onEmailChange} required />}
        <F id="phone" label={p.phone} type="tel" value={v.phone} onChange={set("phone")} />
      </div>
      <div className="space-y-2">
        <Label className={lbl}>{p.userType}</Label>
        <Select required value={v.user_type} onValueChange={(val) => val && set("user_type")(val)}>
          <SelectTrigger data-testid="profile-user-type" className="rounded-sm border-ink/15 focus:ring-amberl h-11 text-left"><SelectValue placeholder={p.userTypePlaceholder} /></SelectTrigger>
          <SelectContent className="bg-white rounded-sm border-ink/10">
            {Object.entries(p.types).map(([k, label]) => <SelectItem key={k} data-testid={`profile-user-type-${k}`} value={k}>{label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      {v.user_type && v.user_type !== "privato" && (
        <div data-testid="profile-company-section" className="space-y-4 border-t border-ink/10 pt-5">
          <p className={sect}>{p.companySection}</p>
          <div className="grid sm:grid-cols-2 gap-6">
            <F id="company-name" label={p.companyName} value={v.company_name} onChange={set("company_name")} required />
            <F id="company-city" label={p.companyCity} value={v.company_city} onChange={set("company_city")} required />
          </div>
          <F id="website" label={p.website} value={v.website} onChange={set("website")} placeholder="https://" />
        </div>
      )}
      {v.user_type && invoiceMode !== "none" && (
        <div className="space-y-4 border-t border-ink/10 pt-5">
          {invoiceMode === "required" ? (
            <p data-testid="profile-invoice-required" className="text-xs text-inksoft">{p.invoiceRequired}</p>
          ) : (
            <label className="flex items-center gap-3 text-sm text-ink cursor-pointer">
              <input data-testid="profile-invoice-toggle" type="checkbox" checked={v.invoice_requested} onChange={(e) => set("invoice_requested")(e.target.checked)} className="w-4 h-4 accent-[#B8875A]" />
              {p.invoiceToggle}
            </label>
          )}
          {showInvoice && (
            <div data-testid="profile-billing-section" className="space-y-4">
              <p className={sect}>{p.billingSection}</p>
              <div className="grid sm:grid-cols-6 gap-4">
                <div className="sm:col-span-4"><F id="street" label={p.street} value={v.billing.street} onChange={setB("street")} required /></div>
                <div className="sm:col-span-2"><F id="street-number" label={p.streetNumber} value={v.billing.street_number} onChange={setB("street_number")} required /></div>
                <div className="sm:col-span-2"><F id="zip" label={p.zip} value={v.billing.zip} onChange={setB("zip")} required maxLength={10} /></div>
                <div className="sm:col-span-3"><F id="city" label={p.city} value={v.billing.city} onChange={setB("city")} required /></div>
                <div className="sm:col-span-1"><F id="province" label={p.province} value={v.billing.province} onChange={setB("province")} required maxLength={2} /></div>
              </div>
              {v.user_type === "privato" && <F id="fiscal-code" label={p.fiscalCode} value={v.billing.fiscal_code} onChange={setB("fiscal_code")} required maxLength={16} />}
              {isBusiness && (
                <div className="grid sm:grid-cols-2 gap-4">
                  <F id="vat" label={p.vat} value={v.billing.vat} onChange={setB("vat")} required maxLength={13} />
                  <F id="company-fiscal-code" label={p.companyFiscalCode} value={v.billing.company_fiscal_code} onChange={setB("company_fiscal_code")} maxLength={16} />
                  <F id="sdi" label={p.sdi} value={v.billing.sdi} onChange={setB("sdi")} maxLength={7} placeholder="0000000" />
                  <F id="pec" label={p.pec} type="email" value={v.billing.pec} onChange={setB("pec")} />
                </div>
              )}
              {isPA && (
                <div className="grid sm:grid-cols-3 gap-4">
                  <F id="cuu" label={p.cuu} value={v.billing.cuu} onChange={setB("cuu")} required maxLength={6} />
                  <F id="cig" label={p.cig} value={v.billing.cig} onChange={setB("cig")} maxLength={10} />
                  <F id="cup" label={p.cup} value={v.billing.cup} onChange={setB("cup")} maxLength={15} />
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
